import { z } from 'zod';
import { isoDate, trimmed, uuid } from './common.js';

/** Money is always integer paise on the wire (1 rupee = 100 paise). Never a float. */
export const paise = z.number().int().positive().max(100_000_000_000); // up to ₹100 crore
export const ymd = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Use a date like 2026-10-05');
export const period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use a month like 2026-10');

export const accountDto = z.object({
  id: uuid,
  code: z.string(),
  name: z.string(),
  type: z.enum(['asset', 'liability', 'equity', 'income', 'expense']),
  isBank: z.boolean(),
  active: z.boolean(),
});

// ───────────── billing ─────────────

export const chargeHeadDto = z.object({
  id: uuid,
  name: z.string(),
  incomeAccountId: uuid,
  incomeAccountName: z.string(),
  basis: z.enum(['fixed', 'per_sqft']),
  amountPaise: z.number().int(),
  active: z.boolean(),
});
export const updateChargeHead = z.object({
  name: trimmed(2, 80).optional(),
  basis: z.enum(['fixed', 'per_sqft']).optional(),
  amountPaise: z.number().int().min(0).max(100_000_000_000).optional(),
  active: z.boolean().optional(),
});
export const createChargeHead = z.object({
  name: trimmed(2, 80),
  incomeAccountId: uuid,
  basis: z.enum(['fixed', 'per_sqft']).default('fixed'),
  amountPaise: z.number().int().min(0).max(100_000_000_000),
});

export const generateInvoices = z.object({
  period,
  /** Defaults to the 10th of the billing month. */
  dueDate: ymd.optional(),
  /** Defaults to today. */
  issueDate: ymd.optional(),
  /** Bill only these flats. Omit to bill every flat. */
  unitIds: z.array(uuid).min(1).max(2000).optional(),
});
export const generateResult = z.object({
  created: z.number(),
  skippedExisting: z.number(),
  totalPaise: z.number(),
});

export const invoiceLineDto = z.object({ description: z.string(), amountPaise: z.number() });
export const invoiceDto = z.object({
  id: uuid,
  number: z.string(),
  unitId: uuid,
  unitLabel: z.string(),
  period: z.string(),
  periodMonths: z.number(),
  issueDate: z.string(),
  dueDate: z.string(),
  totalPaise: z.number(),
  paidPaise: z.number(),
  balancePaise: z.number(),
  status: z.enum(['open', 'paid', 'void']),
  overdue: z.boolean(),
  lines: z.array(invoiceLineDto),
});
export type InvoiceDto = z.infer<typeof invoiceDto>;

export const listInvoicesQuery = z.object({
  unitId: uuid.optional(),
  period: period.optional(),
  status: z.enum(['open', 'paid', 'void']).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

// ───────────── payments ─────────────

export const paymentMode = z.enum(['upi', 'bank_transfer', 'cheque', 'cash', 'other']);

export const recordPayment = z.object({
  unitId: uuid,
  amountPaise: paise,
  mode: paymentMode,
  receivedOn: ymd,
  /** UTR / cheque no. Required for everything except cash, so the same bank transaction cannot be entered twice. */
  reference: trimmed(3, 60).optional(),
  payerName: trimmed(1, 80).optional(),
  notes: trimmed(1, 300).optional(),
  /** Which bank/cash account it landed in. Defaults to Bank (Cash for cash payments). */
  depositAccountId: uuid.optional(),
});

export const paymentDto = z.object({
  id: uuid,
  receiptNumber: z.string(),
  unitId: uuid,
  unitLabel: z.string(),
  receivedOn: z.string(),
  amountPaise: z.number(),
  mode: paymentMode,
  reference: z.string().nullable(),
  payerName: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(['recorded', 'reversed']),
  /** Part of this payment matched to invoices. */
  allocatedPaise: z.number(),
  /** Part not yet needed by any invoice: held as an advance for the flat. */
  advancePaise: z.number(),
  createdAt: isoDate,
});
export type PaymentDto = z.infer<typeof paymentDto>;

export const listPaymentsQuery = z.object({
  unitId: uuid.optional(),
  from: ymd.optional(),
  to: ymd.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export const reasonBody = z.object({ reason: trimmed(3, 300) });

/** Delete (void) several unpaid bills at once, e.g. a cycle sent by mistake. */
export const voidInvoicesBody = z.object({ ids: z.array(uuid).min(1).max(500), reason: trimmed(3, 300) });
export const voidInvoicesResult = z.object({ voided: z.number(), skippedPaid: z.number() });

// ───────────── expenses ─────────────

export const recordExpense = z.object({
  expenseDate: ymd,
  expenseAccountId: uuid,
  paidFromAccountId: uuid,
  amountPaise: paise,
  payee: trimmed(2, 120),
  reference: trimmed(1, 60).optional(),
  notes: trimmed(1, 300).optional(),
});
export const expenseDto = z.object({
  id: uuid,
  voucherNumber: z.string(),
  expenseDate: z.string(),
  accountName: z.string(),
  paidFromName: z.string(),
  amountPaise: z.number(),
  payee: z.string(),
  reference: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(['recorded', 'reversed']),
});
export type ExpenseDto = z.infer<typeof expenseDto>;
export const listExpensesQuery = z.object({ from: ymd.optional(), to: ymd.optional(), limit: z.coerce.number().int().min(1).max(500).default(100) });

// ───────────── dues, reports, closing ─────────────

export const duesRow = z.object({
  unitId: uuid,
  unitLabel: z.string(),
  invoicedPaise: z.number(),
  paidPaise: z.number(),
  /** Positive = the flat owes this much. Negative = the flat has paid in advance. */
  balancePaise: z.number(),
  oldestUnpaidDue: z.string().nullable(),
  daysOverdue: z.number(),
});
export const duesResponse = z.object({
  items: z.array(duesRow),
  totalDuePaise: z.number(),
  totalAdvancePaise: z.number(),
  overdueUnits: z.number(),
});

export const trialBalanceQuery = z.object({ asOf: ymd });
export const trialBalanceResponse = z.object({
  asOf: z.string(),
  rows: z.array(z.object({ code: z.string(), name: z.string(), type: z.string(), debitPaise: z.number(), creditPaise: z.number() })),
  totalDebitPaise: z.number(),
  totalCreditPaise: z.number(),
  balanced: z.boolean(),
});
export const rangeQuery = z.object({ from: ymd, to: ymd });
export const incomeExpenditureResponse = z.object({
  from: z.string(),
  to: z.string(),
  income: z.array(z.object({ code: z.string(), name: z.string(), amountPaise: z.number() })),
  expenses: z.array(z.object({ code: z.string(), name: z.string(), amountPaise: z.number() })),
  totalIncomePaise: z.number(),
  totalExpensePaise: z.number(),
  /** Income minus expenses. Negative means a deficit. */
  surplusPaise: z.number(),
});

export const lockBooks = z.object({ through: ymd });

// ───────────── resident view ─────────────

export const myFinanceResponse = z.object({
  balancePaise: z.number(),
  invoices: z.array(invoiceDto),
  payments: z.array(paymentDto),
});

// ───────────── maintenance basis, frequency, flat areas ─────────────

export const chargeBasis = z.enum(['fixed', 'per_sqft']);
export const billingFrequency = z.enum(['monthly', 'quarterly', 'half_yearly']);
export const MONTHS_PER_CYCLE = { monthly: 1, quarterly: 3, half_yearly: 6 } as const;

export const unitAreaDto = z.object({ unitId: uuid, unitLabel: z.string(), areaSqft: z.number().nullable() });
export const setUnitAreas = z.object({
  areas: z
    .array(z.object({ unitId: uuid, areaSqft: z.number().positive().max(1_000_000).multipleOf(0.01).nullable() }))
    .min(1)
    .max(2000),
});

/** What one flat pays: the resident's own "how is my maintenance worked out" view. */
export const myMaintenanceUnit = z.object({
  unitId: uuid,
  unitLabel: z.string(),
  areaSqft: z.number().nullable(),
  charges: z.array(
    z.object({
      name: z.string(),
      basis: chargeBasis,
      /** Per flat per month (fixed) or per sq ft per month (per_sqft). */
      ratePaise: z.number(),
      /** null when the charge is per sq ft and the flat's area is not entered yet. */
      perMonthPaise: z.number().nullable(),
    }),
  ),
  perMonthPaise: z.number().nullable(),
  perCyclePaise: z.number().nullable(),
});
export const myMaintenanceResponse = z.object({
  frequency: billingFrequency,
  monthsPerCycle: z.number(),
  units: z.array(myMaintenanceUnit),
});

// ───────────── expense heads ─────────────

export const expenseHeadDto = z.object({ id: uuid, code: z.string(), name: z.string(), active: z.boolean(), isSystem: z.boolean() });
export const createExpenseHead = z.object({ name: trimmed(2, 80) });
export const updateExpenseHead = z.object({ name: trimmed(2, 80).optional(), active: z.boolean().optional() });

// ───────────── where the money went (visible to residents too) ─────────────

export const spendingQuery = z.object({ from: period, to: period });
export const spendingResponse = z.object({
  from: z.string(),
  to: z.string(),
  /** Sum of all flat areas entered; divide a total by this for "per sq ft". 0 when no areas are entered. */
  totalAreaSqft: z.number(),
  /** Flats with no area entered: the per sq ft figure is then spread over fewer flats than exist. */
  unitsMissingArea: z.number(),
  /** Maintenance fund carried in from before the first month. Negative = overspent earlier. */
  openingPaise: z.number(),
  collectedPaise: z.number(),
  spentPaise: z.number(),
  /** openingPaise + collectedPaise - spentPaise: carried forward to the next month. */
  closingPaise: z.number(),
  heads: z.array(z.object({ accountId: uuid, name: z.string(), spentPaise: z.number() })),
  months: z.array(
    z.object({
      month: z.string(),
      openingPaise: z.number(),
      collectedPaise: z.number(),
      spentPaise: z.number(),
      closingPaise: z.number(),
      byHead: z.array(z.object({ accountId: uuid, spentPaise: z.number() })),
    }),
  ),
});
export type SpendingResponse = z.infer<typeof spendingResponse>;
